import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './Button';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'admin' | 'manager' | 'employee' | 'viewer';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Super Admin isolation: super_admin can ONLY access super_admin routes
  // and a few universal routes (like profile)
  // They should NOT see tenant data (OKRs, users, etc.)
  if (user?.role === 'super_admin') {
    // Routes that super_admin can access (not tenant-specific)
    const allowedSuperAdminPaths = ['/profile', '/super-admin'];
    const isAllowedPath = allowedSuperAdminPaths.some(path =>
      location.pathname === path || location.pathname.startsWith(path + '/')
    );

    // If this route requires super_admin OR is in the allowed list, allow it
    if (requiredRole === 'super_admin' || isAllowedPath) {
      return <>{children}</>;
    }

    // Super admin trying to access tenant routes - redirect to their dashboard
    return <Navigate to="/super-admin" replace />;
  }

  // For non-super_admin users: block access to super_admin routes
  if (requiredRole === 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            This area is restricted to platform administrators only.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check role-based access for tenant users
  if (requiredRole && user) {
    // Tenant role hierarchy (super_admin is excluded - handled above)
    const roleHierarchy = {
      viewer: 0,
      employee: 1,
      manager: 2,
      admin: 3,        // Legacy - maps to account_admin
      account_admin: 3,
    };

    const userRoleLevel = roleHierarchy[user.role as keyof typeof roleHierarchy] ?? 0;
    const requiredRoleLevel = roleHierarchy[requiredRole as keyof typeof roleHierarchy] ?? 0;

    if (userRoleLevel < requiredRoleLevel) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to access this page.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
              >
                Go Back
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
